import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { PhoneInput } from '@/components/PhoneInput';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextArea } from '@/components/TextArea';
import { TextInput } from '@/components/TextInput';
import { useEventStore } from '@/stores/useEventStore';
import { Event } from '@/types/event';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

export const UpdateEventGeneralInformations = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { event, updateEvent, loading } = useEventStore();

  const validationSchema = yup.object().shape({
    name: yup.string().required(t('common:validation.name.required')),
    description: yup
      .string()
      .required(t('common:validation.description.required')),
    location: yup.string().required(t('common:validation.location.required')),
    date: yup.string().required(t('common:validation.start_date.required')),
    end_date: yup.string().required(t('common:validation.end_date.required')),
    email: yup.string().email(t('common:validation.email.format')),
    phone: yup.string(),
    type: yup
      .string()
      .oneOf(['public', 'private'])
      .required(t('common:validation.type.required')),
    status: yup
      .string()
      .oneOf(['future', 'ongoing', 'past'])
      .required(t('common:validation.status.required')),
    category: yup.string().nullable(),
    maximum_participants: yup
      .number()
      .nullable()
      .min(1, t('common:validation.maximum_participants.min')),
    show_capacity: yup.boolean().required(t('common:validation.required')),
    organizer: yup.string().nullable(),
    cover: yup.string().nullable(),
  });

  const handleSubmit = async (values: Partial<Event>) => {
    if (!id) return;
    await updateEvent(Number(id), values);
  };

  if (!event) return <Loading />;

  return (
    <>
      <div className="mb-4">
        <Text className="text-lg font-semibold text-secondary">
          {t('updateEvent:general.informations.title')}
        </Text>
      </div>

      <Formik
        initialValues={event}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextInput
                label={t('common:fields.name')}
                placeholder={t(
                  'updateEvent:general.informations.name.placeholder',
                )}
                type="text"
                name="name"
                value={values.name}
                error={touched.name && errors.name}
                isRequired
              />

              <TextInput
                label={t('common:fields.location')}
                placeholder={t(
                  'updateEvent:general.informations.location.placeholder',
                )}
                type="text"
                name="location"
                value={values.location}
                error={touched.location && errors.location}
                isRequired
              />

              <TextInput
                label={t('common:fields.start_date')}
                type="datetime-local"
                name="date"
                value={values.date}
                error={touched.date && errors.date}
                isRequired
              />

              <TextInput
                label={t('common:fields.end_date')}
                type="datetime-local"
                name="end_date"
                value={values.end_date}
                error={touched.end_date && errors.end_date}
                isRequired
              />

              <TextInput
                label={t('common:fields.email')}
                placeholder={t(
                  'updateEvent:general.informations.email.placeholder',
                )}
                type="email"
                name="email"
                value={values.email}
                error={touched.email && errors.email}
              />

              <PhoneInput
                label={t('common:fields.phone')}
                placeholder={t(
                  'updateEvent:general.informations.phone.placeholder',
                )}
                name="phone"
                error={!!errors.phone && !!touched.phone}
              />

              <SelectInput
                label={t('common:fields.type')}
                placeholder={t(
                  'updateEvent:general.informations.type.placeholder',
                )}
                name="type"
                defaultValue={values.type}
                options={[
                  { label: t('common:type.public'), value: 'public' },
                  { label: t('common:type.private'), value: 'private' },
                ]}
                onChange={handleChange}
                error={errors.type}
                isRequired
              />

              <SelectInput
                label={t('common:fields.status')}
                placeholder={t(
                  'updateEvent:general.informations.status.placeholder',
                )}
                name="status"
                defaultValue={values.status}
                options={[
                  { label: t('common:status.future'), value: 'future' },
                  { label: t('common:status.ongoing'), value: 'ongoing' },
                  { label: t('common:status.past'), value: 'past' },
                ]}
                onChange={handleChange}
                error={errors.status}
                isRequired
              />

              <TextInput
                label={t('common:fields.category')}
                placeholder={t(
                  'updateEvent:general.informations.category.placeholder',
                )}
                name="category"
                type="text"
                value={values.category}
                error={errors.category}
              />

              <TextInput
                label={t('common:fields.organizer')}
                placeholder={t(
                  'updateEvent:general.informations.organizer.placeholder',
                )}
                name="organizer"
                type="text"
                value={values.organizer}
                error={errors.organizer}
              />
              <TextInput
                label={t('createEvent:maximum_participants')}
                placeholder={t('createEvent:maximum_participants')}
                name="maximum_participants"
                type="number"
                error={errors.maximum_participants}
              />
              <SelectInput
                label={t('createEvent:show_capacity')}
                name="show_capacity"
                placeholder={t('createEvent:show_capacity')}
                onChange={handleChange}
                defaultValue={values.show_capacity}
                options={[
                  { value: true, label: t('common:yes') },
                  { value: false, label: t('common:no') },
                ]}
              />
            </div>

            <TextArea
              label={t('common:fields.description')}
              placeholder={t(
                'updateEvent:general.informations.description.placeholder',
              )}
              name="description"
              error={errors.description}
              isRequired
            />

            <div className="mt-4">
              <Button
                className="w-full lg:w-auto"
                type="submit"
                disabled={isSubmitting || loading}
              >
                {t('common:update')}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};
